// src/components/dataSells/NotificationCenter.jsx
import React from 'react';
import {
    FaCheckCircle,
    FaExclamationCircle,
    FaExclamationTriangle,
    FaInfoCircle,
    FaTimes,
    FaBell
} from 'react-icons/fa';

/**
 * Componente para mostrar notificaciones del proceso de importación
 */
const NotificationCenter = ({ notifications, onRemoveNotification, onClearAll }) => {
    const getIcon = (type) => {
        switch (type) {
            case 'success':
                return <FaCheckCircle className="text-green-500 text-xl" />;
            case 'error':
                return <FaExclamationCircle className="text-red-500 text-xl" />;
            case 'warning':
                return <FaExclamationTriangle className="text-yellow-500 text-xl" />;
            case 'info':
            default:
                return <FaInfoCircle className="text-blue-500 text-xl" />;
        }
    };

    const getBackgroundColor = (type) => {
        switch (type) {
            case 'success':
                return 'bg-green-50 border-green-200';
            case 'error':
                return 'bg-red-50 border-red-200';
            case 'warning':
                return 'bg-yellow-50 border-yellow-200';
            case 'info':
            default:
                return 'bg-blue-50 border-blue-200';
        }
    };

    const formatTime = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diff = Math.floor((now - date) / 1000); // diferencia en segundos

        if (diff < 60) {
            return 'Hace unos segundos';
        } else if (diff < 3600) {
            const minutes = Math.floor(diff / 60);
            return `Hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
        } else if (diff < 86400) {
            const hours = Math.floor(diff / 3600);
            return `Hace ${hours} hora${hours > 1 ? 's' : ''}`;
        } else {
            return date.toLocaleString('es-ES', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    };

    if (notifications.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4 flex items-center">
                    <FaBell className="mr-2 text-gray-400" />
                    Centro de Notificaciones
                </h2>
                <div className="text-center py-8">
                    <FaBell className="mx-auto text-4xl text-gray-300 mb-3" />
                    <p className="text-gray-500">No hay notificaciones</p>
                    <p className="text-sm text-gray-400 mt-1">
                        Las notificaciones sobre tus importaciones aparecerán aquí
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold flex items-center">
                    <FaBell className="mr-2 text-blue-500" />
                    Centro de Notificaciones
                    <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                        {notifications.length}
                    </span>
                </h2>
                {notifications.length > 0 && (
                    <button
                        onClick={onClearAll}
                        className="text-sm text-gray-500 hover:text-gray-700"
                    >
                        Limpiar todo
                    </button>
                )}
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
                {notifications.map(notification => (
                    <div
                        key={notification.id}
                        className={`border rounded-lg p-4 ${getBackgroundColor(notification.type)}`}
                    >
                        <div className="flex items-start">
                            <div className="flex-shrink-0 mt-0.5">
                                {getIcon(notification.type)}
                            </div>
                            <div className="ml-3 flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900">
                                    {notification.message}
                                </p>

                                {/* Detalles adicionales */}
                                {notification.details && (
                                    <div className="mt-2 text-xs text-gray-600">
                                        {/* Campos faltantes */}
                                        {notification.details.camposFaltantes && (
                                            <div className="mt-1">
                                                <span className="font-medium">Campos faltantes:</span>{' '}
                                                {notification.details.camposFaltantes.join(', ')}
                                            </div>
                                        )}

                                        {/* Campos no reconocidos */}
                                        {notification.details.camposNoReconocidos && (
                                            <div className="mt-1">
                                                <span className="font-medium">Campos no reconocidos:</span>{' '}
                                                {notification.details.camposNoReconocidos.join(', ')}
                                            </div>
                                        )}

                                        {/* Ventas creadas */}
                                        {notification.details.ventasCreadas !== undefined && (
                                            <div className="mt-1">
                                                <span className="font-medium">Ventas creadas:</span>{' '}
                                                {notification.details.ventasCreadas}
                                            </div>
                                        )}

                                        {/* Ventas duplicadas */}
                                        {notification.details.ventasDuplicadas !== undefined && notification.details.ventasDuplicadas > 0 && (
                                            <div className="mt-1">
                                                <span className="font-medium">Ventas duplicadas:</span>{' '}
                                                {notification.details.ventasDuplicadas}
                                            </div>
                                        )}

                                        {/* Tickets actualizados */}
                                        {notification.details.ticketsActualizados !== undefined && (
                                            <div className="mt-1">
                                                <span className="font-medium">Tickets actualizados:</span>{' '}
                                                {notification.details.ticketsActualizados}
                                            </div>
                                        )}

                                        {/* Productos creados */}
                                        {notification.details.productosCreados !== undefined && (
                                            <div className="mt-1">
                                                <span className="font-medium">Productos creados:</span>{' '}
                                                {notification.details.productosCreados}
                                            </div>
                                        )}

                                        {/* Errores */}
                                        {notification.details.errores && notification.details.errores.length > 0 && (
                                            <div className="mt-2">
                                                <span className="font-medium">Errores:</span>
                                                <ul className="list-disc list-inside mt-1 space-y-1">
                                                    {notification.details.errores.map((error, idx) => (
                                                        <li key={idx} className="text-xs">{error}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <p className="mt-2 text-xs text-gray-500">
                                    {formatTime(notification.timestamp)}
                                </p>
                            </div>
                            <button
                                onClick={() => onRemoveNotification(notification.id)}
                                className="flex-shrink-0 ml-3 text-gray-400 hover:text-gray-600"
                            >
                                <FaTimes />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default NotificationCenter;

